import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, CircleStop, Eye, Gift, Play, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useGate } from '@/client/hooks/useGate';
import { PERMISSIONS } from '@/client/config/permissions';
import { useActivateCycleMutation, useCloseCycleMutation, useReviewCycleQuery } from '@/client/api/hr/perf';
import { useCompSummaryQuery } from '@/client/api/hr/comp';
import { useHrLevelsQuery } from '@/client/api/hr/people';
import {
  GOAL_STATUS_BADGE,
  GOAL_STATUS_LABEL,
  REVIEW_CYCLE_STATUS_BADGE,
  REVIEW_CYCLE_STATUS_LABEL,
  REVIEW_STATUS_BADGE,
  REVIEW_STATUS_LABEL,
  type Employee,
  type PerformanceReview,
} from '@/client/types/hr';
import { DeleteConfirmDialog, EmployeeAvatar, EmployeeLink, EmptyState, Panel, ProgressBar, RowMenu, StatusBadge } from './components/hr-ui';
import { BonusDialog, CompChangeDialog } from './components/CompDialogs';
import { ReviewText, Stars } from './components/ReviewText';
import { formatDay } from './components/leave-format';
import { useEmployees } from './hr-store';

function ReviewCell({ review, writer }: { review?: PerformanceReview; writer?: Employee | null }) {
  if (!review) return <span className="text-xs text-gray-400">—</span>;
  return (
    <div className="space-y-0.5">
      <StatusBadge label={REVIEW_STATUS_LABEL[review.status]} className={REVIEW_STATUS_BADGE[review.status]} />
      {review.status === 'submitted' && <Stars rating={review.rating} />}
      {writer && review.type === 'manager' && <p className="text-[11px] text-gray-400">by {writer.name}</p>}
    </div>
  );
}

const ReviewCycleDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const cycleQ = useReviewCycleQuery(id);
  const employees = useEmployees();
  const levels = useHrLevelsQuery().data ?? [];
  const canManage = useGate(PERMISSIONS.perf.manage).allowed;
  const canPropose = useGate(PERMISSIONS.comp.manage).allowed;
  const comp = useCompSummaryQuery(canPropose);
  const activate = useActivateCycleMutation();
  const close = useCloseCycleMutation();
  const [confirmKind, setConfirmKind] = useState<'activate' | 'close' | null>(null);
  const [viewing, setViewing] = useState<Employee | null>(null);
  const [raiseFor, setRaiseFor] = useState<{ employee: Employee; reviewId: string } | null>(null);
  const [bonusFor, setBonusFor] = useState<{ employee: Employee; reviewId: string } | null>(null);

  const cycle = cycleQ.data;
  const byId = useMemo(() => new Map(employees.map((e) => [e.id, e])), [employees]);

  useBreadcrumbs([
    { label: 'Admin', href: '/admin' },
    { label: 'Business', href: '/admin/business' },
    { label: 'People', href: '/admin/human-resource' },
    { label: 'Review cycles', href: '/admin/human-resource/performance/cycles' },
    { label: cycle?.name ?? 'Cycle' },
  ]);

  const rows = useMemo(() => {
    if (!cycle) return [];
    const ids = [...new Set(cycle.reviews.map((r) => r.employeeId))];
    return ids
      .map((eid) => ({
        employee: byId.get(eid) ?? null,
        self: cycle.reviews.find((r) => r.employeeId === eid && r.type === 'self'),
        manager: cycle.reviews.find((r) => r.employeeId === eid && r.type === 'manager'),
        goals: cycle.goals.filter((g) => g.employeeId === eid),
      }))
      .sort((a, b) => (b.manager?.rating ?? 0) - (a.manager?.rating ?? 0) || (a.employee?.name ?? '').localeCompare(b.employee?.name ?? ''));
  }, [cycle, byId]);

  if (cycleQ.isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <EmptyState label="Loading review cycle…" />
      </div>
    );
  }
  if (cycleQ.isError || !cycle) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <EmptyState label={cycleQ.isError ? `Could not load the cycle: ${(cycleQ.error as Error)?.message}` : 'Review cycle not found.'} />
      </div>
    );
  }

  const s = cycle.stats ?? { self: { submitted: 0, total: 0 }, manager: { submitted: 0, total: 0 } };
  const closed = cycle.status === 'closed';
  const viewRow = rows.find((r) => r.employee?.id === viewing?.id);

  const runConfirm = () => {
    const mut = confirmKind === 'close' ? close : activate;
    mut.mutate(cycle.id, {
      onSuccess: (res) =>
        toast.success(confirmKind === 'close' ? 'Cycle closed. Manager reviews are shared.' : `${res.opened ?? 0} review${res.opened === 1 ? '' : 's'} opened`),
      onError: (err) => toast.error((err as Error).message || 'Could not update the cycle'),
    });
    setConfirmKind(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <button type="button" onClick={() => navigate('/admin/human-resource/performance/cycles')} className="mb-4 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
        <ArrowLeft className="h-4 w-4" /> Review cycles
      </button>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">{cycle.name}</h1>
            <StatusBadge label={REVIEW_CYCLE_STATUS_LABEL[cycle.status]} className={REVIEW_CYCLE_STATUS_BADGE[cycle.status]} />
          </div>
          <p className="text-sm text-gray-500">
            {formatDay(cycle.startDate)} – {formatDay(cycle.endDate)}
            {cycle.dueDate && ` · reviews due ${formatDay(cycle.dueDate)}`}
          </p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            {cycle.status === 'upcoming' && (
              <Button className="gap-2" onClick={() => setConfirmKind('activate')}>
                <Play className="h-4 w-4" /> Activate
              </Button>
            )}
            {cycle.status === 'active' && (
              <>
                <Button variant="outline" className="gap-2" onClick={() => setConfirmKind('activate')}>
                  <Play className="h-4 w-4" /> Add missing reviews
                </Button>
                <Button variant="outline" className="gap-2 text-red-600 hover:text-red-700" onClick={() => setConfirmKind('close')}>
                  <CircleStop className="h-4 w-4" /> Close cycle
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        {(
          [
            ['Self reviews', s.self],
            ['Manager reviews', s.manager],
          ] as const
        ).map(([label, v]) => (
          <div key={label} className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
            <p className="text-sm text-gray-500">{label} submitted</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">
              {v.submitted} / {v.total}
            </p>
            <ProgressBar value={v.total ? (v.submitted / v.total) * 100 : 0} className="mt-2" />
          </div>
        ))}
        <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
          <p className="text-sm text-gray-500">Goals in this cycle</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">
            {cycle.goals.filter((g) => g.status === 'completed').length} / {cycle.goals.length} completed
          </p>
        </div>
      </div>

      {cycle.status === 'upcoming' ? (
        <EmptyState label="Not started. Activate the cycle to open everyone's reviews." />
      ) : rows.length === 0 ? (
        <EmptyState label="No reviews in this cycle." />
      ) : (
        <Panel title={closed ? 'Results (highest manager rating first)' : 'Progress'}>
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="py-2 font-medium">Employee</th>
                <th className="py-2 font-medium">Self review</th>
                <th className="py-2 font-medium">Manager review</th>
                <th className="py-2 font-medium">Goals</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map(({ employee, self, manager, goals }) => {
                if (!employee) return null;
                const proposeFrom = manager?.status === 'submitted' ? manager.id : null;
                const hasPay = comp.data?.rows.find((r) => r.employeeId === employee.id)?.current;
                return (
                  <tr key={employee.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setViewing(employee)}>
                    <td className="py-2.5">
                      <div className="flex items-center gap-2">
                        <EmployeeAvatar employee={employee} size="sm" />
                        <EmployeeLink employee={employee} />
                      </div>
                    </td>
                    <td className="py-2.5">
                      <ReviewCell review={self} />
                    </td>
                    <td className="py-2.5">
                      <ReviewCell review={manager} writer={manager ? byId.get(manager.reviewerId) : null} />
                    </td>
                    <td className="py-2.5 text-xs text-gray-500">
                      {goals.length ? `${goals.filter((g) => g.status === 'completed').length} of ${goals.length} done` : '—'}
                    </td>
                    <td className="py-2.5 text-right">
                      <RowMenu
                        label={`Actions for ${employee.name}`}
                        items={[
                          { label: 'Read reviews', icon: Eye, onSelect: () => setViewing(employee) },
                          {
                            label: 'Propose raise or promotion',
                            icon: TrendingUp,
                            hidden: !closed || !canPropose || !proposeFrom || !hasPay,
                            onSelect: () => setRaiseFor({ employee, reviewId: proposeFrom as string }),
                          },
                          {
                            label: 'Propose bonus',
                            icon: Gift,
                            hidden: !closed || !canPropose || !proposeFrom,
                            onSelect: () => setBonusFor({ employee, reviewId: proposeFrom as string }),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {closed && canPropose && (
            <p className="mt-3 text-xs text-gray-500">
              Raises and bonuses proposed from here link back to the review, and still need someone else to approve them in Compensation.
            </p>
          )}
        </Panel>
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {viewing?.name} · {cycle.name}
            </DialogTitle>
          </DialogHeader>
          {viewRow && (
            <div className="space-y-5">
              {(['self', 'manager'] as const).map((k) => {
                const r = viewRow[k];
                return (
                  <div key={k}>
                    <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">
                      {k === 'self' ? 'Self review' : `Manager review${r ? ` · ${byId.get(r.reviewerId)?.name ?? ''}` : ''}`}
                      {r && ` · ${REVIEW_STATUS_LABEL[r.status]}`}
                    </p>
                    {r ? <ReviewText review={r} /> : <p className="text-sm text-gray-400">No {k} review in this cycle.</p>}
                  </div>
                );
              })}
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">Goals</p>
                {viewRow.goals.length === 0 ? (
                  <p className="text-sm text-gray-400">No goals tied to this cycle.</p>
                ) : (
                  <ul className="space-y-2">
                    {viewRow.goals.map((g) => (
                      <li key={g.id} className="flex items-center justify-between gap-2 text-sm">
                        <span className="text-gray-800">{g.title}</span>
                        <StatusBadge label={GOAL_STATUS_LABEL[g.status]} className={GOAL_STATUS_BADGE[g.status]} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CompChangeDialog
        open={!!raiseFor}
        mode="propose"
        employee={raiseFor?.employee ?? null}
        current={comp.data?.rows.find((r) => r.employeeId === raiseFor?.employee.id)?.current ?? null}
        levels={levels}
        reviewId={raiseFor?.reviewId}
        onClose={() => setRaiseFor(null)}
      />
      <BonusDialog open={!!bonusFor} employee={bonusFor?.employee ?? null} reviewId={bonusFor?.reviewId} onClose={() => setBonusFor(null)} />

      <DeleteConfirmDialog
        open={!!confirmKind}
        onOpenChange={(v) => !v && setConfirmKind(null)}
        title={confirmKind === 'close' ? `Close ${cycle.name}?` : cycle.status === 'active' ? 'Open reviews for people added since?' : `Activate ${cycle.name}?`}
        description={
          confirmKind === 'close'
            ? `${s.self.total - s.self.submitted + s.manager.total - s.manager.submitted} review(s) are still drafts and will stay unsubmitted. Submitted manager reviews become visible to the people they are about. This cannot be undone.`
            : 'Everyone gets a self review, and everyone with a manager gets a manager review. They write them in My workplace.'
        }
        actionLabel={confirmKind === 'close' ? 'Close cycle' : 'Activate'}
        danger={confirmKind === 'close'}
        onConfirm={runConfirm}
      />
    </div>
  );
};

export default ReviewCycleDetailPage;
